<template>
  <div class="product-basketbutton">
    <slot name="additionalconfig" :setAdditionalBasketConfig="setAdditionalBasketConfig" />
    <b-button class="basketbutton" variant="custom" :class="{ 'small': small }" @click="addToCart">
      <div class="basketbutton-quantity" @click.stop>
        <b-dropdown v-show="!quantity.customMode" :text="dropdownText" size="sm" dropup no-flip>
          <b-dropdown-item v-for="item in dropdownItems" :key="item.value" @click="setQuantity(item.type, item.value)">{{ item.text }}</b-dropdown-item>
        </b-dropdown>

        <b-input v-show="quantity.customMode" ref="quantityControl" v-model="quantity.value" @blur="toggleQuantityCustomMode(false)" />
      </div>

      <div class="basketbutton-text">
        <slot />
      </div>

      <div class="basketbutton-quantity invisible" aria-hidden="true">
        <b-dropdown v-show="!quantity.customMode" :text="dropdownText" disabled tabindex="-1"></b-dropdown>
        <b-input v-show="quantity.customMode" v-model="quantity.value" disabled tabindex="-1" />
      </div>
    </b-button>
    <b-modal
             ref="marketingProductsModal"
             :title="marketingProductsModalTitle"
             centered
             ok-only
             :ok-title="marketingProductsModalCloseLabel"
             :hide-header-close="false"
             dialog-class="marketing-products-modal-dialog"
             lazy
             @hidden="onMarketingProductsModalHidden">
      <div v-if="marketingModalProducts.length" class="marketing-products-modal-content">
        <div class="marketing-products-list">
          <c-linked-marketing-product
                                      v-for="marketingProduct in marketingModalProducts"
                                      :key="marketingProduct.id"
                                      :product-id="marketingProduct.id"
                                      :image-url="marketingProduct.imageUrl"
                                      :display-name="marketingProduct.displayName"
                                      :article-number="marketingProduct.articleNumber"
                                      :is-store-window="marketingProduct.isStoreWindow"
                                      :basket-translations="marketingModalNestedBasketTranslations"
                                      :required-additional-properties-validation="marketingProduct.requiredAdditionalPropertiesValidation"
                                      :error-toast-title="marketingProduct.errorToastTitle"
                                      :enable-marketing-product-upsell="false" />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import http from '@/$plugins/http';
import CLinkedMarketingProduct from '@/components/LinkedMarketingProduct';
import { COMPONENT_PRODUCTBUYBUTTON_DROPDOWN_MAX_ITEMS, COMPONENT_PRODUCTBUYBUTTON_QUANTITY_DEFAULTS, MARKETING_PRODUCT_STORE_WINDOW_FAMILY_ID } from '@/constants';

import { closest } from '@/assets/js/helper/array';

export default {
  name: 'ProductBuybutton',
  components: {
    CLinkedMarketingProduct
  },
  props: {
    productId: {
      type: String,
      required: true
    },
    quantityinfo: {
      type: Object,
      default: () => ({})
    },
    quantityPostFixSingular: {
      type: String,
      default: ''
    },
    quantityPostFixPlural: {
      type: String,
      default: ''
    },
    small: {
      type: Boolean,
      default: false
    },
    requiredAdditionalPropertiesValidation: {
      type: Object,
      default: () => ({})
    },
    errorToastTitle: {
      type: String,
      default: ''
    },
    enableMarketingProductUpsell: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      quantity: {
        customMode: false,
        value: null,
        displayText: '',
        tempValue: null
      },
      additionalConfigurations: {},
      marketingModalProducts: [],
      marketingProductFlow: {
        lastTriggeredProductId: null
      }
    }
  },
  computed: {
    quantityOptions() {
      // must be >= 1, fallback = 1
      const max = Math.trunc(Math.max(1, Number(this.quantityinfo.max || COMPONENT_PRODUCTBUYBUTTON_QUANTITY_DEFAULTS.max)))
      // must be >= 1 and <= max, fallback = 1 or max
      const min = Math.trunc(Math.max(1, Math.min(max, Number(this.quantityinfo.min || COMPONENT_PRODUCTBUYBUTTON_QUANTITY_DEFAULTS.min))))
      // must be >= 1, fallback = 1
      const step = Math.trunc(Math.max(1, Number(this.quantityinfo.step || COMPONENT_PRODUCTBUYBUTTON_QUANTITY_DEFAULTS.step)))

      return {
        min,
        max,
        range: max - min,
        step
      }
    },
    quantitySteps() {
      // add 1 for initial step (e.g. quantityOptions.min)
      const stepCount = Math.trunc(this.quantityOptions.range / this.quantityOptions.step) + 1
      return Array
        .from(Array(stepCount).keys())
        .map(step => {
          const stepValue = (step === 0 ? this.quantityOptions.min : this.quantityOptions.min + step * this.quantityOptions.step)
          return {
            value: stepValue,
            text: stepValue +
              (stepValue === 1
                ? (this.quantityPostFixSingular ? ` ${this.quantityPostFixSingular}` : '')
                : (this.quantityPostFixPlural ? ` ${this.quantityPostFixPlural}` : ''))
          }
        })
    },
    quantityInit() {
      // must by one >= 1 and <= quantitySteps.length, fallback = 0 or quantitySteps.length - 1
      const initialStep = Number(Math.min(this.quantitySteps.length, Math.max(1, this.quantityinfo.initialStep || COMPONENT_PRODUCTBUYBUTTON_QUANTITY_DEFAULTS.initialStep))) - 1
      return this.quantitySteps[initialStep]
    },
    dropdownText() {
      return (this.quantity.displayText || '').toString()
    },
    dropdownItems() {
      return this.quantitySteps
        .slice(0, COMPONENT_PRODUCTBUYBUTTON_DROPDOWN_MAX_ITEMS).map(ddi => ({ type: 'number', value: ddi.value, text: ddi.text }))
        .concat(({ type: 'custom', text: this.quantitySteps.length > COMPONENT_PRODUCTBUYBUTTON_DROPDOWN_MAX_ITEMS ? this.$t(`${this.tPath}.customquantitylabel`) : [] }))
    },
    marketingModalNestedBasketTranslations() {
      const p = this.tPath
      const t = (key) => this.$t(`${p}.${key}`)
      return {
        customQuantityLabel: t('customquantitylabel'),
        marketingProductsModalTitle: t('marketingproductsmodaltitle'),
        marketingProductsModalCloseLabel: t('marketingproductsmodalcloselabel'),
        addToBasket: t('addtobasket'),
        deliveryTimeSlot: t('deliverytimeslot'),
        marketingproductdetailNodateselected: t('marketingproductdetail.nodateselected'),
        marketingproductdetailErrortitle: t('marketingproductdetail.errortitle'),
        notifyMe: t('notifyme'),
        notifyMeSuccess: t('notifymesuccess'),
        notifyMeUnsubscribed: t('notifymeunsubscribed'),
        notifyMeAlreadySubscribed: t('notifymealreadysubscribed'),
        notifyMeError: t('notifymeerror'),
        notifyMeUnsubscribeError: t('notifymeunsubscribeerror')
      }
    },
    marketingProductsModalTitle() {
      return this.$t(`${this.tPath}.marketingproductsmodaltitle`)
    },
    marketingProductsModalCloseLabel() {
      return this.$t(`${this.tPath}.marketingproductsmodalcloselabel`)
    }
  },
  methods: {
    getApiResult(response) {
      return response?.data?.result ??
        response?.data?.Result ??
        response?.result ??
        response?.Result ??
        null
    },
    getApiPayload(response) {
      return this.getApiResult(response) ??
        response?.data ??
        response ??
        null
    },
    normalizeMarketingProductsPayload(payload) {
      if (!payload) return []
      if (Array.isArray(payload)) return payload
      if (typeof payload !== 'object') return []

      const nested = payload.results ??
        payload.Results ??
        payload.items ??
        payload.Items ??
        payload.hits ??
        payload.Hits ??
        payload.docs ??
        payload.Docs ??
        payload.data ??
        payload.Data

      if (nested) return this.normalizeMarketingProductsPayload(nested)

      const values = Object.values(payload)
      if (!values.length) return []

      // Treat unknown object wrappers as product collections only when the values are objects.
      const allObjects = values.every(v => v && typeof v === 'object')
      return allObjects ? values : [payload]
    },
    getRelationRows(item) {
      if (!item) return []
      if (Array.isArray(item.datas)) return item.datas
      if (Array.isArray(item.Datas)) return item.Datas
      return [item]
    },
    getRelationRowId(row) {
      return row?.id ?? row?.Id ?? row?.targetId ?? row?.TargetId ?? null
    },
    getRelationRowFamilyId(row) {
      return row?.familyId ?? row?.FamilyId ?? row?.familyid ?? null
    },
    collectRelationFamilyMap(relations) {
      const map = new Map()
      const visitItem = (item) => {
        for (const row of this.getRelationRows(item)) {
          const id = this.getRelationRowId(row)
          const fam = this.getRelationRowFamilyId(row)
          if (id && fam) {
            map.set(String(id), String(fam))
          }
        }
      }
      const list = Array.isArray(relations) ? relations : []
      for (const item of list) {
        visitItem(item)
      }
      return map
    },
    mergeMarketingRelationFamilyMaps(...maps) {
      const out = new Map()
      for (const m of maps) {
        if (!m) continue
        m.forEach((v, k) => {
          if (v) out.set(k, v)
        })
      }
      return out
    },
    setQuantity(type, value = this.quantityOptions.min) {
      if (type === 'number') {
        this.quantity.value = value
        this.quantity.displayText = this.quantitySteps.find(x => x.value === value)?.text
      } else {
        this.toggleQuantityCustomMode(true)
      }
    },
    toggleQuantityCustomMode(state = false) {
      if (state) {
        this.quantity.tempValue = this.quantity.value
        this.quantity.value = null
        this.quantity.customMode = true

        this.$nextTick(() => {
          this.$refs.quantityControl.focus()
        })
      } else {
        const quantityValue = Number(this.quantity.value)
        const isNonNumberic = !/^-?([0-9.]){1,}$/.test(this.quantity.value)
        const isOutOfRange = quantityValue < this.quantityOptions.min && quantityValue > this.quantityOptions.max
        const isOutOfSteps = !this.quantitySteps.some(itm => itm.value === quantityValue)

        if (isNonNumberic || isOutOfRange) {
          this.quantity.value = this.quantity.tempValue
          this.quantity.tempValue = null
          this.quantity.customMode = false
        } else if (isOutOfSteps) {
          this.quantity.value = closest(this.quantitySteps.map(x => x.value), quantityValue)
        }
      }
    },
    async addToCart() {
      let additionalConfigs = null
      const validationErrors = []
      if (Object.keys(this.additionalConfigurations)) {
        additionalConfigs = this.additionalConfigurations
      }
      if (Object.keys(this.requiredAdditionalPropertiesValidation).length !== 0) {
        for (var key in this.requiredAdditionalPropertiesValidation) {
          if (additionalConfigs[key] === null || additionalConfigs[key] === undefined) {
            validationErrors.push(this.requiredAdditionalPropertiesValidation[key])
          }
        }
      }
      if (validationErrors.length > 0) {
        validationErrors.forEach((error, i) => this.$bvToast.toast(error, {
          title: this.errorToastTitle,
          autoHideDelay: 5000,
          variant: 'danger',
          solid: true
        }))
        return
      }
      const addToCartRequest = this.$store.dispatch('shoppingcart/addItem', { productId: this.productId, additionalConfigurations: additionalConfigs, quantity: this.quantity.value })
      const marketingProductsRequest = this.enableMarketingProductUpsell
        ? this.loadMarketingProductsForProduct(this.productId)
        : Promise.resolve([])

      await addToCartRequest
      if (this.enableMarketingProductUpsell) {
        const marketingProducts = await marketingProductsRequest
        const filteredMarketingProducts = this.filterMarketingProducts(marketingProducts)
        this.triggerMarketingProductFlow(filteredMarketingProducts)
        this.openMarketingProductsModalIfNeeded(filteredMarketingProducts)
      }
      this.quantity.value = this.quantityInit.value
      this.quantity.displayText = this.quantityInit.text
      this.quantity.customMode = false
    },
    async loadMarketingProductsForProduct(productGtin) {
      if (!productGtin) {
        return []
      }

      try {
        const productResponse = await http({
          method: 'get',
          url: `/catalog/api/Products/bygtin/${encodeURIComponent(productGtin)}`
        })

        const collectRelationIds = relations => {
          const ids = []
          const list = Array.isArray(relations) ? relations : []
          for (const item of list) {
            for (const row of this.getRelationRows(item)) {
              const id = this.getRelationRowId(row)
              if (id) ids.push(String(id))
            }
          }
          return ids
        }

        const productResult = this.getApiPayload(productResponse) || {}
        const references = productResult?.references ?? productResult?.References
        const matches = productResult?.matches ?? productResult?.Matches
        const isMatchedBy = productResult?.isMatchedBy ?? productResult?.IsMatchedBy
        const referencedIds = [...new Set([
          ...collectRelationIds(references),
          ...collectRelationIds(matches),
          ...collectRelationIds(isMatchedBy)
        ])]

        if (!referencedIds.length) {
          return []
        }

        const marketingProductsResponse = await http({
          method: 'post',
          url: '/search/api/ProductAndMarketingProducts/searchbyids',
          data: {
            ids: referencedIds,
            page: 1,
            pageSize: referencedIds.length
          }
        })

        const products = this.getApiPayload(marketingProductsResponse)
        const list = this.normalizeMarketingProductsPayload(products)
        const familyById = this.mergeMarketingRelationFamilyMaps(
          this.collectRelationFamilyMap(references),
          this.collectRelationFamilyMap(matches),
          this.collectRelationFamilyMap(isMatchedBy)
        )
        return list.map(p => {
          const sid = String(p?.id ?? '')
          const fam = familyById.get(sid)
          return fam ? { ...p, familyId: fam } : p
        })
      } catch (error) {
        this.$emit('marketing-products-load-failed', {
          productId: this.productId,
          trigger: 'add-to-basket-click',
          error
        })
        return []
      }
    },
    filterMarketingProducts(products = []) {
      const list = this.normalizeMarketingProductsPayload(products)
      return list.filter(product => {
        const discriminator = (product?.type ?? product?.Type ?? product?.typeId ?? product?.TypeId ?? product?.urlType ?? product?.productType ?? product?.ProductType ?? '').toString().toLowerCase()
        return discriminator === 'marketingproduct'
      })
    },
    getMarketingProductImageUrl(marketingProduct = {}) {
      return marketingProduct?.imgSrc ||
        (typeof marketingProduct?.image === 'string' ? marketingProduct.image : null) ||
        marketingProduct?.image?.url ||
        marketingProduct?.imageUrl ||
        marketingProduct?.thumbnailUrl ||
        null
    },
    resolveLocalizedValue(value) {
      if (!value) return ''
      if (typeof value === 'string') return value
      if (typeof value !== 'object') return ''

      const locale = this?.$i18n?.locale || ''
      if (locale && typeof value[locale] === 'string') {
        return value[locale]
      }

      const shortLocale = locale.includes('-') ? locale.split('-')[0] : locale
      if (shortLocale) {
        const localeMatchKey = Object.keys(value).find(key => key.toLowerCase().startsWith(shortLocale.toLowerCase()))
        if (localeMatchKey && typeof value[localeMatchKey] === 'string') {
          return value[localeMatchKey]
        }
      }

      const firstString = Object.values(value).find(entry => typeof entry === 'string')
      return firstString || ''
    },
    getMarketingProductDisplayName(marketingProduct = {}) {
      return this.resolveLocalizedValue(marketingProduct?.localizedName) ||
        marketingProduct?.name ||
        marketingProduct?.title ||
        ''
    },
    getMarketingProductArticleNumber(marketingProduct = {}) {
      return marketingProduct?.articleNumber ||
        marketingProduct?.articleNo ||
        marketingProduct?.number ||
        marketingProduct?.gtin ||
        marketingProduct?.id ||
        ''
    },
    toMarketingModalProduct(marketingProduct = {}, index = 0) {
      const imageUrl = this.getMarketingProductImageUrl(marketingProduct)
      const displayName = this.getMarketingProductDisplayName(marketingProduct)
      const articleNumber = this.getMarketingProductArticleNumber(marketingProduct)
      const familyId = (marketingProduct.familyId || marketingProduct.FamilyId || '').toString()
      const isStoreWindow = familyId === MARKETING_PRODUCT_STORE_WINDOW_FAMILY_ID
      const modalT = this.marketingModalNestedBasketTranslations

      return {
        id: String(marketingProduct.id || articleNumber || displayName || `marketing-product-${index}`),
        imageUrl: imageUrl || '',
        displayName,
        articleNumber,
        isStoreWindow,
        requiredAdditionalPropertiesValidation: isStoreWindow
          ? { dateStart: modalT.marketingproductdetailNodateselected }
          : {},
        errorToastTitle: isStoreWindow ? modalT.marketingproductdetailErrortitle : ''
      }
    },
    mapMarketingProductsForModal(marketingProducts = []) {
      return marketingProducts.map((item, index) => this.toMarketingModalProduct(item, index))
    },
    openMarketingProductsModalIfNeeded(marketingProducts = []) {
      if (!Array.isArray(marketingProducts) || marketingProducts.length === 0) {
        return
      }

      this.marketingModalProducts = this.mapMarketingProductsForModal(marketingProducts)
      const modalRef = this.$refs.marketingProductsModal
      if (modalRef && typeof modalRef.show === 'function') {
        modalRef.show()
      }
    },
    onMarketingProductsModalHidden() {
      this.marketingModalProducts = []
    },
    triggerMarketingProductFlow(marketingProducts = []) {
      this.marketingProductFlow.lastTriggeredProductId = this.productId
      this.$emit('marketing-products-load-requested', {
        productId: this.productId,
        trigger: 'add-to-basket-click',
        marketingProducts
      })
    },
    setAdditionalBasketConfig(additionalConfigs) {
      Object.keys(additionalConfigs).forEach(key => {
        this.additionalConfigurations[key] = additionalConfigs[key]
      })
    }
  },
  created() {
    this.quantity.value = this.quantityInit.value
    this.quantity.displayText = this.quantityInit.text
  }
}
</script>

<style lang="scss">
$productbasketbutton-padding-y: $btn-padding-y * 0.4 !default;
$productbasketbutton-padding-x: $productbasketbutton-padding-y !default;
$productbasketbutton-bg: $primary !default;
$productbasketbutton-border: transparent !default;
$productbasketbutton-font-weight: $font-weight-bold !default;
$productbasketbutton-line-height: 1.1 !default;
$productbasketbutton-hover-bg: darken($productbasketbutton-bg, 5%) !default;
$productbasketbutton-hover-border: darken($productbasketbutton-border, 5%) !default;

$productbasketbutton-quantity-width: $spacer * 4 !default;
$productbasketbutton-quantity-height: $input-height-sm !default;

$productbasketbutton-quantity-toggle-padding-y: 0 !default;
$productbasketbutton-quantity-toggle-padding-x: $btn-padding-x * 0.5 !default;
$productbasketbutton-quantity-toggle-bg: darken($productbasketbutton-bg, 2.5%) !default;
$productbasketbutton-quantity-toggle-border: darken($productbasketbutton-border, 2.5%) !default;
$productbasketbutton-quantity-toggle-hover-bg: darken($productbasketbutton-quantity-toggle-bg, 6%) !default;
$productbasketbutton-quantity-toggle-hover-border: darken($productbasketbutton-quantity-toggle-border, 6%) !default;

$productbasketbutton-quantity-menu-bg: $productbasketbutton-bg !default;
$productbasketbutton-quantity-menu-border: $productbasketbutton-border !default;
$productbasketbutton-quantity-menu-caret-size: $spacer * 0.6 !default;
$productbasketbutton-quantity-menu-item-padding-y: $dropdown-item-padding-y !default;
$productbasketbutton-quantity-menu-item-padding-x: $dropdown-item-padding-x * 0.6 !default;
$productbasketbutton-quantity-menu-item-hover-bg: $productbasketbutton-hover-bg !default;

$productbasketbutton-quantity-control-padding-y: $productbasketbutton-quantity-toggle-padding-y !default;
$productbasketbutton-quantity-control-padding-x: $productbasketbutton-quantity-toggle-padding-x !default;
$productbasketbutton-quantity-control-bg: $productbasketbutton-quantity-toggle-bg !default;
$productbasketbutton-quantity-control-border: $productbasketbutton-quantity-toggle-bg !default;
$productbasketbutton-quantity-control-hover-bg: $productbasketbutton-quantity-toggle-hover-bg !default;
$productbasketbutton-quantity-control-hover-border: $productbasketbutton-quantity-toggle-hover-border !default;
$productbasketbutton-quantity-control-focus-bg: $productbasketbutton-quantity-toggle-hover-bg !default;
$productbasketbutton-quantity-control-focus-border: $white !default;

$productbasketbutton-text-padding-y: 0 !default;
$productbasketbutton-text-padding-x: $spacer !default;

.product-basketbutton {
  .basketbutton {
    @include button-variant($productbasketbutton-bg, $productbasketbutton-border, $productbasketbutton-hover-bg, $productbasketbutton-hover-border);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: $productbasketbutton-padding-y $productbasketbutton-padding-x;
    width: 100%;
    font-weight: $productbasketbutton-font-weight;
    line-height: $productbasketbutton-line-height;

    .basketbutton-quantity {
      width: $productbasketbutton-quantity-width;
      height: $productbasketbutton-quantity-height;

      .dropdown {
        width: 100%;
        height: 100%;

        .dropdown-toggle {
          @include button-variant($productbasketbutton-quantity-toggle-bg, $productbasketbutton-quantity-toggle-border, $productbasketbutton-quantity-toggle-hover-bg, $productbasketbutton-quantity-toggle-hover-border);
          padding-top: $productbasketbutton-quantity-toggle-padding-y;
          padding-bottom: $productbasketbutton-quantity-toggle-padding-y;
          padding-left: $productbasketbutton-quantity-toggle-padding-x;
          height: 100%;
          background-image: escape-svg(str-replace($dropdown-toggle-icon, "fill='#{$dropdown-toggle-color}'", "fill='#{color-yiq($productbasketbutton-quantity-toggle-bg)}'"));
          box-shadow: none !important;
        }

        .dropdown-menu {
          margin-bottom: 0;
          margin-left: 50%;
          min-width: 0;
          background-color: $productbasketbutton-quantity-menu-bg;
          border-color: $productbasketbutton-quantity-menu-border;
          color: color-yiq($productbasketbutton-quantity-menu-bg);
          transform: translate3d(-50%, calc(-100% - (#{$productbasketbutton-padding-y} + #{$productbasketbutton-quantity-menu-caret-size} + 2px)), 0) !important;

          &:after {
            display: block;
            content: '';
            position: absolute;
            top: 100%;
            left: 50%;
            width: 0;
            height: 0;
            border-width: $productbasketbutton-quantity-menu-caret-size $productbasketbutton-quantity-menu-caret-size 0;
            border-style: solid solid none;
            border-color: $productbasketbutton-quantity-menu-bg transparent transparent;
            transform: translateX(-50%);
          }

          .dropdown-item {
            padding: $productbasketbutton-quantity-menu-item-padding-y $productbasketbutton-quantity-menu-item-padding-x;
            color: inherit;

            &:hover {
              background-color: $productbasketbutton-quantity-menu-item-hover-bg;
            }
          }
        }
      }

      .form-control {
        padding: $productbasketbutton-quantity-control-padding-y $productbasketbutton-quantity-control-padding-x;
        height: 100%;
        background-color: $productbasketbutton-quantity-control-bg;
        border-color: $productbasketbutton-quantity-control-border;
        border-radius: $dropdown-border-radius;
        color: color-yiq($productbasketbutton-quantity-control-bg);
        text-align: center;
        transition: $transition-base;

        &:focus {
          background-color: $productbasketbutton-quantity-control-focus-bg !important;
          border-color: $productbasketbutton-quantity-control-focus-border !important;
        }
      }
    }

    .basketbutton-text {
      padding: $productbasketbutton-text-padding-y $productbasketbutton-text-padding-x;
    }

    &.small {
      .basketbutton-quantity {
        width: 2.5rem;

        .dropdown-toggle {
          padding-left: 0.4rem;
          padding-right: 1.5rem;
        }

        &.invisible {
          display: none;
        }
      }
    }

    &:hover {
      .basketbutton-quantity {
        .dropdown {
          .dropdown-toggle {
            color: color-yiq($productbasketbutton-quantity-toggle-hover-bg);
            @include gradient-bg($productbasketbutton-quantity-toggle-hover-bg);
            border-color: $productbasketbutton-quantity-toggle-hover-border;
          }
        }

        .form-control {
          background-color: $productbasketbutton-quantity-control-hover-bg;
          border-color: $productbasketbutton-quantity-control-hover-bg;
        }
      }
    }
  }
}

.marketing-products-list {
  display: flex;
  flex-direction: column;
  gap: $spacer * 0.5;

  .marketing-products-row {
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
    margin-bottom: $spacer * 0.5;

    &:last-child {
      margin-bottom: 0;
    }

    .marketing-products-cell {
      min-width: 0;
    }

    .image-cell {
      flex: 0 0 4.5rem;
      padding-right: $spacer * 0.75;
    }

    .info-cell {
      flex: 1 1 auto;
      padding-right: $spacer * 0.75;
    }

    .action-cell {
      flex: 0 0 auto;
      text-align: right;
    }
  }

  .marketing-products-modal-image,
  .marketing-products-modal-image-placeholder {
    width: 3.25rem;
    height: 3.25rem;
    margin-bottom: 0;
    border-radius: $border-radius;
  }

  .marketing-products-modal-image {
    object-fit: cover;
  }

  .marketing-products-modal-image-placeholder {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: $gray-100;
    color: $gray-600;
  }

  .marketing-products-modal-name {
    font-weight: $font-weight-bold;
    line-height: 1.2;
  }

  .marketing-products-modal-article-number {
    margin-top: $spacer * 0.2;
    font-size: $font-size-sm;
    color: $gray-600;
  }

  .marketing-products-modal-add-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.1rem;
    height: 2.1rem;
    padding: 0;
  }
}

.marketing-products-modal-dialog {
  .marketing-products-row .action-cell {
    white-space: nowrap;
  }

  .purchase-basket-select {
    overflow: visible;
  }

  .purchase-basket-select .basketbutton {
    align-items: stretch;
  }

  .purchase-basket-select .basketbutton-quantity {
    display: flex;
    align-items: stretch;
    height: auto;
    min-height: 2.1rem;
  }

  .purchase-basket-select .basketbutton-quantity .dropdown {
    height: 100%;
  }

  .purchase-basket-select .basketbutton-quantity .dropdown-toggle {
    height: 100%;
    display: inline-flex;
    align-items: center;
  }

  .purchase-basket-select .basketbutton-text {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding-left: $spacer * 0.35;
    padding-right: $spacer * 0.35;
    min-height: 2.1rem;
  }

  .purchase-basket-select .basketbutton-text .bi {
    font-size: 1rem;
    line-height: 1;
    vertical-align: middle;
  }

  .purchase-basket-select .dropdown-toggle {
    min-height: 1.55rem;
    padding-top: 0;
    padding-bottom: 0;
  }
}
</style>
